/**
 * This node's federation peers and its own federation identity, for the
 * superadmin's Federation tab (components/Account/FederationPeers.jsx).
 *
 * Reads GET /api/federation/peers and /api/federation/identity. Both are
 * superadmin-only, so a 403 on the list doubles as the "am I the server
 * admin" signal: `isServerAdmin=false` and callers hide the panel. Any other
 * error is surfaced via `error`.
 *
 * A peer is a node: `addPeer` takes its API address (and the node's slug when
 * its deployment serves several on one address), the backend reads its
 * capability document, records it approved here and handshakes so it records
 * this node in turn; `handshake` repeats that; `remove` forgets it. The
 * identity side: what this node is to its peers, and `createKey` gives it a
 * signing key of its own instead of the deployment's.
 *
 *   const { peers, identity, isServerAdmin, loading, approve, block, unblock,
 *           addPeer, handshake, remove, createKey } = useFederationPeers();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function useFederationPeers() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [peers, setPeers] = useState([]);
    const [identity, setIdentity] = useState(null);
    const [isServerAdmin, setIsServerAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `silent` refreshes after a mutation without flipping `loading`, so the
    // table doesn't dim + spin on every click; only the first load shows it.
    const load = useCallback(({ silent = false } = {}) => {
        if (!userId) { setLoading(false); return Promise.resolve(); }
        if (!silent) setLoading(true);
        const params = { user_id: userId };
        return api.get('/api/federation/peers', { params })
            .then(data => {
                setPeers(Array.isArray(data) ? data : []);
                setIsServerAdmin(true);
                setError(null);
                // Only a server admin gets this far, so the identity read
                // cannot be the thing that 403s; a failure there just leaves
                // the identity card out.
                return api.get('/api/federation/identity', { params })
                    .then(setIdentity)
                    .catch(() => setIdentity(null));
            })
            .catch(err => {
                if (err.status === 403) {
                    setIsServerAdmin(false);   // not the server admin — panel stays hidden
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const act = useCallback((serverId, action) => {
        const path = `/api/federation/peers/${encodeURIComponent(serverId)}/${action}`;
        return api.post(path, undefined, { params: { user_id: userId } }).then(() => load({ silent: true }));
    }, [api, userId, load]);

    const addPeer = useCallback(({ base_url, node_slug }) => (
        api.post('/api/federation/peers', { base_url, node_slug: node_slug || null }, { params: { user_id: userId } })
            .then(peer => load({ silent: true }).then(() => peer))
    ), [api, userId, load]);

    const remove = useCallback(serverId => (
        api.del(`/api/federation/peers/${encodeURIComponent(serverId)}`, { params: { user_id: userId } })
            .then(() => load({ silent: true }))
    ), [api, userId, load]);

    const createKey = useCallback(() => (
        api.post('/api/federation/identity/key', undefined, { params: { user_id: userId } })
            .then(data => { setIdentity(data); return data; })
    ), [api, userId]);

    return {
        peers,
        identity,
        isServerAdmin,
        loading,
        error,
        refresh: load,
        approve: serverId => act(serverId, 'approve'),
        block: serverId => act(serverId, 'block'),
        unblock: serverId => act(serverId, 'unblock'),
        handshake: serverId => act(serverId, 'handshake'),
        addPeer,
        remove,
        createKey,
    };
}

export default useFederationPeers;
